import { ChevronDown, ChevronRight, Wrench } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { EquipmentTreeNode } from '@/features/equipment/service';
import { criticalityColor } from '@/features/equipment/types';
import { cn } from '@/lib/utils';

interface EquipmentTreeProps {
  nodes: EquipmentTreeNode[];
  /** Optional: ids initially expanded (default: all roots collapsed). */
  defaultExpandedIds?: string[];
}

/**
 * Custom recursive tree — no external library. Each node renders:
 *   - a chevron toggle (or 0.75rem spacer if leaf)
 *   - a Wrench icon
 *   - a clickable tag (font-mono) + name link to /equipment/:id
 *   - a color-coded criticality badge
 *   - an inactive marker (•) when the equipment is archived
 *
 * Expand/collapse state lives in a single Set<string> in the root
 * component — no per-node state, no prop drilling beyond the
 * (toggle, expanded) callbacks. Initial expand list lets callers
 * pre-open the path to a deep-linked equipment.
 */
export function EquipmentTree({
  nodes,
  defaultExpandedIds = [],
}: EquipmentTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(defaultExpandedIds),
  );

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <ul className="space-y-1" role="tree">
      {nodes.map((n) => (
        <TreeNode
          key={n.id}
          node={n}
          depth={0}
          expanded={expanded}
          onToggle={toggle}
        />
      ))}
    </ul>
  );
}

interface TreeNodeProps {
  node: EquipmentTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
}

function TreeNode({ node, depth, expanded, onToggle }: TreeNodeProps) {
  const { t } = useTranslation();
  const children = node.children ?? [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(node.id);

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isOpen : undefined}>
      <div
        className={cn(
          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50',
          !node.isActive && 'opacity-60',
        )}
        style={{ paddingLeft: `${depth * 1.25 + 0.5}rem` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={isOpen ? t('common.close') : t('common.openMenu')}
          >
            {isOpen ? (
              <ChevronDown className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden />
            )}
          </button>
        ) : (
          <span className="inline-block h-5 w-5" aria-hidden />
        )}

        <Wrench className="h-4 w-4 text-muted-foreground" aria-hidden />

        <Link
          to={`/equipment/${node.id}`}
          className="flex flex-1 items-center gap-2 hover:underline"
        >
          <span className="font-mono text-xs text-primary">{node.tag}</span>
          <span className="truncate">{node.name}</span>
        </Link>

        <span
          className={cn(
            'inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium',
            criticalityColor(node.criticality),
          )}
          aria-label={t('equipment.fields.criticality')}
        >
          {node.criticality}
        </span>

        {!node.isActive && (
          <span
            className="text-xs text-muted-foreground"
            aria-label={t('equipment.status.inactive')}
          >
            •
          </span>
        )}
      </div>

      {hasChildren && isOpen && (
        <ul className="space-y-1" role="group">
          {children.map((c) => (
            <TreeNode
              key={c.id}
              node={c}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
